/** /zh/architecture-decision-record — Chinese version of the ADR landing page.
 *  Native zh content, same structure as ArchitectureDecisionRecord.tsx. */
import { MarketingShell } from './MarketingShell';
import { TemplateCta } from './TemplateCta';

export function ArchitectureDecisionRecordZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/architecture-decision-record"
      documentTitle="架构决策记录（ADR）：一直保持最新的那一种 · Draftlize"
      documentDescription="传统的架构决策记录（ADR）是 repo 里的一份 markdown——写一次，然后随着代码漂移而萎缩、过时。Draftlize 让 ADR 成为 AI coding agent 能读也能写的活记录：决策一变，实现状态自动翻成 drift_detected。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">指南 · 架构决策记录</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            架构决策记录，<br/>
            <em>不会烂在 repo 里的那一种。</em>
          </h1>
          <p className="lede">
            架构决策记录（ADR）要回答一个代码库里最贵的问题：<em>「为什么是这么搭的？」</em>这套实践本身是对的——一份小小的、带日期的记录，写下当时的背景、做了什么决定、以及后果。问题出在 substrate。一份 ADR 是 check 进 repo 的 markdown 文件，在做决定那天写下，之后再没人打开。代码在演进，决策悄悄不再匹配现实，ADR 就成了考古文物。Draftlize 保留这份记录本身——但让它成为 AI coding agent 在动代码之前会先读的东西，并在它所记录的决策不再成立的那一刻，自动翻成 <em>drift_detected</em>。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">TL;DR</span>
            <h2 className="serif">一份 markdown vs <em>一份活的 ADR。</em></h2>
          </div>
          <p>放在 <code>docs/adr/0007-use-event-sourcing.md</code> 里的 ADR，提交那天是对的，之后就慢慢和运行中的系统分叉。解法不是更严的 review checklist，而是一个知道哪些记录还匹配代码、并在某条不匹配时告诉你的 substrate。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>repo 里的 markdown ADR</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>记录本身</td><td className="ours yes">结构化卡片：背景、决策、后果、依赖</td><td className="partial">一份手填的 markdown 模板</td></tr>
            <tr><td>状态字段</td><td className="ours yes">活的——自己翻成 drift_detected</td><td className="no">手动改；通常还写着「Accepted」</td></tr>
            <tr><td>决策变了，代码知道吗</td><td className="ours yes">依赖它的记录自动标记 drift</td><td className="no">没有连接，文件一直骗人直到有人发现</td></tr>
            <tr><td>coding agent 用得上吗</td><td className="ours yes">经 MCP 原生读 + 写</td><td className="partial">你指给它，它顶多读个纯文本</td></tr>
            <tr><td>动代码前会被读</td><td className="ours yes">agent 先拉出相关 ADR</td><td className="no">没人打开 <code>docs/adr/</code></td></tr>
            <tr><td>谁来保持它诚实</td><td className="ours yes">substrate 自动追踪</td><td className="no">一份没人执行的 review checklist</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">为什么 ADR 会<em>萎缩。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">写一次，永不回看</h3>
            <p>Michael Nygard 在 2011 年提出架构决策记录，是为了捕捉<em>「为什么」</em>——一个结构性决定背后的背景和取舍。格式没错。但 ADR 归档在 <code>docs/adr/</code>，它的读者是一个永远不会打开这个目录的未来工程师。一份没人回头读的记录，做不到它唯一的那件事。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">代码漂移了，记录没跟着动</h3>
            <p>ADR-0007 写着「账本用 event sourcing」。十八个月后，半个账本又变回了 CRUD——在三个 PR 和一条 Slack 里定下来的。状态那栏还写着 <em>Accepted</em>。这份记录现在会主动误导下一个相信它的人——这比没有记录更糟。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">「保持最新」抢不过「发版」</h3>
            <p>更新 ADR 是个跟「合 PR」抢时间的手动杂活，而 PR 永远赢。于是记录冻结在写下的那一刻，系统却一直在动，<code>docs/adr/</code> 和 <code>main</code> 之间的鸿沟每个 sprint 都在变宽。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">一份会<em>自我维护</em>的 ADR 模板。</h2>
        </div>
        <p style={{ maxWidth: 720, color: 'var(--ink-1)', fontSize: 16, lineHeight: 1.65, margin: '0 0 32px' }}>
          一份标准 ADR 有五个字段。Draftlize 五个全保留——并把那个总是骗人的字段，<em>Status</em>，从「你忘了改的东西」变成「substrate 替你维护的东西」。
        </p>
        <div className="problem-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
          <div className="problem-col">
            <span className="num" style={{ fontSize: 13, letterSpacing: '0.08em' }}>TITLE</span>
            <h3 className="serif" style={{ fontSize: 19 }}>决定了什么</h3>
            <p>一个简短、可寻址的名字。在 Draftlize 里它是一张有稳定 ID 的卡片，能从 spec、thread 或 agent 引用为<em>「依据 ADR-0007」</em>——一个真实的链接，而不是一个你得 grep 的文件名。</p>
          </div>
          <div className="problem-col">
            <span className="num" style={{ fontSize: 13, letterSpacing: '0.08em' }}>STATUS</span>
            <h3 className="serif" style={{ fontSize: 19 }}>会骗人的那一栏</h3>
            <p>在 markdown 里，Status 靠手动改，永远停在 <em>Accepted</em>。在 Draftlize 里它是活的：上游决策一动，实现就把自己翻成 <em>drift_detected</em>，于是不用任何人更新，记录也讲真话。</p>
          </div>
          <div className="problem-col">
            <span className="num" style={{ fontSize: 13, letterSpacing: '0.08em' }}>CONTEXT</span>
            <h3 className="serif" style={{ fontSize: 19 }}>为什么会有这个决定</h3>
            <p>做这个决定时在起作用的各种力量。它连着它所依赖的那些决策——所以当<em>那些</em>变了，这份记录会知道：自己的前提已经动了。</p>
          </div>
          <div className="problem-col">
            <span className="num" style={{ fontSize: 13, letterSpacing: '0.08em' }}>DECISION</span>
            <h3 className="serif" style={{ fontSize: 19 }}>选了什么，又否了什么</h3>
            <p>你选了什么、排除了什么。备选方案是卡片的一部分——所以半年后没人会重新争论一个你早已关掉的选项。</p>
          </div>
          <div className="problem-col">
            <span className="num" style={{ fontSize: 13, letterSpacing: '0.08em' }}>CONSEQUENCES</span>
            <h3 className="serif" style={{ fontSize: 19 }}>它让你背上了什么</h3>
            <p>这个决定带来的后果，好的坏的都算。继承这些后果的依赖卡片都连着——当这份记录在它们脚下变动时，它们会标记 drift。</p>
          </div>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">当读者是<em>一个 coding agent。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">它在写代码前先读 ADR</h3>
            <p>经 MCP 接入的 Claude Code 或 Cursor，会在动一个模块之前先拉出相关记录——于是「这里为什么用 event sourcing」背后的决策，正好在最关键的那一刻进入上下文，而不是埋在没人打开的目录里。那个永不回来的读者，现在成了那个永不跳过任何一张卡片的读者。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">它也会把 ADR 写回去</h3>
            <p>当 agent 在任务中途做了一个结构性决定，它可以把一条新 ADR 记成一张卡片——title、context、decision、consequences——不用离开手头的活去打开一个 markdown 文件。记录之所以会被写下，是因为写它只是一次 tool call，而不是一次上下文切换。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">漂移会浮出来，而不是藏起来</h3>
            <p>每条记录都带显式依赖。改一个上游决策，每条建立在它之上的记录都翻成 <em>drift_detected</em>——就像构建系统让一个改动文件的所有下游失效一样。决策和代码之间的鸿沟，不再是你在复盘时才发现的东西。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          ADR 这套实践从来不是问题。问题是一份记录没法知道：自己其实已经错了。
        </blockquote>
        <cite>五个字段都留下。把它们搬到一个会替你维护那个「会骗人字段」的 substrate 上。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常见问题</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: '什么是架构决策记录（ADR）？', a: 'ADR 是对一个重要架构决策的简短带日期的记录——背景、拍板、后果。这个做法由 Michael Nygard 在 2011 年提出，用来记下系统为什么这样搭，让后来的工程师不必从代码里反推当初的理由。' },
            { q: '标准 ADR 模板里有什么？', a: '五个字段：标题、状态、背景、决策、后果。标题命名这次拍板，状态记它是提议、已接受还是被取代，背景记当时在起作用的各种力，决策讲清选了什么、否了什么，后果记它让你承担了什么。' },
            { q: 'ADR 为什么会过时？', a: '因为它是签进 repo 的静态 markdown，写一次、很少再打开。代码在演进，决策悄悄和现实对不上了，但状态字段还写着「已接受」——于是这份记录反而会误导下一个信它的人，比没有记录更糟。' },
            { q: 'Draftlize 怎么让 ADR 不漂移？', a: 'Draftlize 保留同样五个字段，但把记录做成 AI 编码 agent 在动代码前会先读、拍板后会写回的卡片。上游决策一动，实现状态就自己翻成 drift_detected——那个总在撒谎的字段「状态」，不用任何人去改就保持诚实。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">用 $5 <em>免费开始你的 ADR。</em></h2>
        </div>
        <div className="trust-strip">
          <span>注册送 $5</span>
          <span>按量付费</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            建一个项目，把你已有的 ADR 作为卡片导进来，再经 MCP 接上你的 coding agent——让记录在动代码之前就被读到，并在决策一动时标记 drift。
          </p>
          <a className="btn-primary" href="/">用 $5 免费开始</a>
        </div>
      </section>
      <TemplateCta template="decision-log" locale="zh" />
    </MarketingShell>
  );
}
